import { apiClient } from './apiClient';
import type { AuditPlot, AuditPlotFormValues } from '../../types/audit';

/**
 * Error response structure from API
 */
interface ApiError {
  message: string;
  status?: number;
  originalError?: any;
}

/**
 * Audit Plot API Service
 * Handles all audit plot-related API operations
 */
export class AuditPlotService {
  /**
   * Get all plots for a specific audit
   * 
   * @param auditId - The ID of the audit
   * @returns Promise with array of audit plots
   * 
   * @example
   * ```typescript
   * const plots = await auditPlotService.getPlotsByAudit(123);
   * ```
   */
  async getPlotsByAudit(auditId: number): Promise<AuditPlot[]> {
    try {
      const response = await apiClient.get<AuditPlot[]>(
        `/audit-plots/?audit_id=${auditId}`
      );
      
      return response.data;
    } catch (error: any) {
      throw this.handleError(error, 'error.plot.fetch_failed');
    }
  }

  /**
   * Get a single plot by ID
   * 
   * @param plotId - The ID of the plot
   * @returns Promise with the audit plot
   * 
   * @example
   * ```typescript
   * const plot = await auditPlotService.getPlot('uuid-123');
   * ```
   */
  async getPlot(plotId: string): Promise<AuditPlot> {
    try {
      const response = await apiClient.get<AuditPlot>(
        `/audit-plots/${plotId}/`
      );
      
      return response.data;
    } catch (error: any) {
      throw this.handleError(error, 'error.plot.fetch_failed');
    }
  }

  /**
   * Create a new audit plot
   * 
   * @param data - The plot data to create
   * @returns Promise with the created audit plot
   * 
   * @example
   * ```typescript
   * const newPlot = await auditPlotService.createPlot({
   *   name: 'Parcela 1',
   *   audit_id: 123,
   *   surface: 2.5,
   *   // ... other fields
   * });
   * ```
   */
  async createPlot(data: AuditPlotFormValues): Promise<AuditPlot> {
    try {
      const response = await apiClient.post<AuditPlot>(
        '/audit-plots/',
        data,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      
      return response.data;
    } catch (error: any) {
      throw this.handleError(error, 'error.plot.create_failed');
    }
  }

  /**
   * Update an existing audit plot
   * 
   * @param plotId - The ID of the plot to update
   * @param data - The partial plot data to update
   * @returns Promise with the updated audit plot
   * 
   * @example
   * ```typescript
   * const updatedPlot = await auditPlotService.updatePlot('uuid-123', {
   *   name: 'Parcela 1 Actualizada',
   *   surface: 3.0,
   * });
   * ```
   */
  async updatePlot(
    plotId: string,
    data: Partial<AuditPlotFormValues>
  ): Promise<AuditPlot> {
    try {
      const response = await apiClient.patch<AuditPlot>(
        `/audit-plots/${plotId}/`,
        data,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      
      return response.data;
    } catch (error: any) {
      throw this.handleError(error, 'error.plot.update_failed');
    }
  }

  /**
   * Delete an audit plot
   * 
   * @param plotId - The ID of the plot to delete
   * @param auditId - The ID of the audit (for validation)
   * @returns Promise that resolves when deletion is complete
   * 
   * @example
   * ```typescript
   * await auditPlotService.deletePlot('uuid-123', 123);
   * ```
   */
  async deletePlot(plotId: string, auditId: number): Promise<void> {
    try {
      await apiClient.delete(
        `/audit-plots/${plotId}/?audit_id=${auditId}`
      );
    } catch (error: any) {
      throw this.handleError(error, 'error.plot.delete_failed');
    }
  }

  /**
   * Fusiona 2+ AuditPlots en uno solo. La primera de la lista conserva
   * su identidad; las demás se absorben. Sus plantings se re-linkean
   * al target con `ordering += offset` y los `AuditPlot` absorbidos
   * se destruyen. Contra-parte al bug histórico en el que una parcela
   * doblada quedaba representada por 2 filas distintas.
   *
   * Bloqueado si la auditoría está locked (backend devuelve 403).
   *
   * @param auditId — auditoría dueña de las parcelas.
   * @param targetId — AuditPlot que conserva identidad.
   * @param absorbedIds — AuditPlots a fusionar en el target.
   * @returns { target_id, plantings_count } (fila del target actualizada).
   */
  async mergePlots(
    auditId: number,
    targetId: number,
    absorbedIds: number[],
  ): Promise<{ target_id: number; plantings_count: number }> {
    try {
      const { data } = await apiClient.post<{
        target_id: number;
        plantings_count: number;
      }>(`/audit-plots/merge/`, {
        audit_id: auditId,
        target_id: targetId,
        absorbed_ids: absorbedIds,
      });
      return data;
    } catch (error: any) {
      throw this.handleError(error, 'error.plot.merge_failed');
    }
  }

  /**
   * Get geometry for an audit plot
   * 
   * If the plot doesn't have geometry, tries to find another plot
   * with the same name in the same farm and returns its geometry.
   * 
   * @param plotId - The ID of the plot
   * @returns Promise with geometry data
   * 
   * @example
   * ```typescript
   * const geometryData = await auditPlotService.getPlotGeometry('uuid-123');
   * if (geometryData.geom) {
   *   // Use geometry
   * }
   * ```
   */
  async getPlotGeometry(plotId: string): Promise<{
    geom: GeoJSON.Geometry | null;
    source: 'current_plot' | 'similar_plot' | 'none';
    source_plot_id?: number;
  }> {
    try {
      const response = await apiClient.get(
        `/audit-plots/${plotId}/geometry/`
      );
      
      return response.data;
    } catch (error: any) {
      throw this.handleError(error, 'error.plot.fetch_failed');
    }
  }

  /**
   * Get reference plots from the farm associated with the audit
   * 
   * These plots are used as visual reference in the map editor to help
   * users draw the correct geometry for the audit plot.
   * 
   * @param plotId - The ID of the audit plot
   * @returns Promise with GeoJSON FeatureCollection
   * 
   * @example
   * ```typescript
   * const referencePlots = await auditPlotService.getReferencePlots('uuid-123');
   * // Use referencePlots.features to display on map
   * ```
   */
  async getReferencePlots(plotId: string): Promise<{
    type: 'FeatureCollection';
    features: GeoJSON.Feature[];
    count: number;
  }> {
    try {
      const response = await apiClient.get(
        `/audit-plots/${plotId}/reference-plots/`
      );
      
      return response.data;
    } catch (error: any) {
      throw this.handleError(error, 'error.plot.fetch_failed');
    }
  }

  /**
   * Get historical geometries from PlotReal for the farm
   * 
   * Returns PlotReal records with geometry from previous seasons,
   * useful for validating or reusing geometry from previous years.
   * 
   * @param plotId - The ID of the audit plot
   * @returns Promise with GeoJSON FeatureCollection
   */
  async getHistoricalGeometry(plotId: string): Promise<{
    type: 'FeatureCollection';
    features: GeoJSON.Feature[];
    count: number;
    audit_plot_name: string;
    farm_name: string;
  }> {
    try {
      const response = await apiClient.get(
        `/audit-plots/${plotId}/historical-geometry/`
      );
      
      return response.data;
    } catch (error: any) {
      throw this.handleError(error, 'error.plot.fetch_failed');
    }
  }

  /**
   * Get route geometry performed on the same day as the audit
   *
   * @param plotId - The ID of the audit plot
   * @returns Promise with GeoJSON FeatureCollection of LineString routes
   */
  async getRouteGeometry(plotId: string): Promise<{
    type: 'FeatureCollection';
    features: GeoJSON.Feature[];
    count: number;
    audit_date: string | null;
    planning_name: string | null;
  }> {
    try {
      const response = await apiClient.get(
        `/audit-plots/${plotId}/route-geometry/`
      );

      return response.data;
    } catch (error: any) {
      throw this.handleError(error, 'error.plot.fetch_failed');
    }
  }

  /**
   * Handle API errors and provide translated error messages
   * 
   * @param error - The error object from the API
   * @param defaultMessageKey - Default translation key for the error message
   * @returns Formatted error object
   */
  private handleError(error: any, defaultMessageKey: string): ApiError {
    // If error already has a message property (from apiClient interceptor)
    if (error.message) {
      return {
        message: error.message,
        status: error.status,
        originalError: error.originalError,
      };
    }

    // Handle axios error response
    if (error.response?.data) {
      const errorData = error.response.data;
      
      // Extract error message from various possible formats
      let message = defaultMessageKey;
      
      if (typeof errorData === 'string') {
        message = errorData;
      } else if (errorData.error) {
        message = errorData.error;
      } else if (errorData.detail) {
        message = errorData.detail;
      } else if (errorData.message) {
        message = errorData.message;
      } else if (errorData.errors) {
        // Handle validation errors
        if (Array.isArray(errorData.errors)) {
          message = errorData.errors.join(', ');
        } else if (typeof errorData.errors === 'object') {
          message = Object.values(errorData.errors).flat().join(', ');
        }
      }

      return {
        message,
        status: error.response.status,
        originalError: error,
      };
    }

    // Handle network or other errors
    return {
      message: error.message || defaultMessageKey,
      originalError: error,
    };
  }
}

/**
 * Singleton instance of AuditPlotService
 */
export const auditPlotService = new AuditPlotService();

export default auditPlotService;
